import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Pencil, Trash2, Calendar, User, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import TextNoteDialog from '../components/TextNoteDialog';
import DeleteDialog from '../components/DeleteDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TextNoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor } = useUser();
  const [note, setNote] = useState(null);
  const [company, setCompany] = useState(null);
  const [contact, setContact] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNoteData();
  }, [id]);

  const fetchNoteData = async () => {
    try {
      const noteRes = await axios.get(`${API}/text-notes/${id}`);
      setNote(noteRes.data);

      if (noteRes.data.company_id) {
        const companyRes = await axios.get(`${API}/companies/${noteRes.data.company_id}`);
        setCompany(companyRes.data);
      }
      if (noteRes.data.contact_id) {
        const contactRes = await axios.get(`${API}/contacts/${noteRes.data.contact_id}`);
        setContact(contactRes.data);
      }
      if (noteRes.data.opportunity_id) {
        const oppRes = await axios.get(`${API}/opportunities/${noteRes.data.opportunity_id}`);
        setOpportunity(oppRes.data);
      }
    } catch (error) {
      console.error('Error fetching note data:', error);
      toast.error('Error al cargar la nota');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/text-notes/${id}`);
      toast.success('Nota eliminada correctamente');
      navigate('/text-notes');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Error al eliminar la nota');
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      meeting: 'bg-blue-100 text-blue-800',
      call: 'bg-green-100 text-green-800',
      reflexion: 'bg-amber-100 text-amber-800',
      evento: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      meeting: 'Reunión',
      call: 'Llamada',
      reflexion: 'Reflexión',
      evento: 'Evento'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-sm text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="p-8">
        <div className="text-sm text-muted-foreground">Nota no encontrada</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="text-note-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/text-notes')}
        className="mb-4"
        data-testid="back-to-notes-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Notas de Texto
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <Badge className={getTypeColor(note.type)}>{getTypeLabel(note.type)}</Badge>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              {note.date}
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" data-testid="note-title">{note.title}</h1>
          {note.tags && note.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {note.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {isEditor && (
            <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="edit-note-btn">
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
          {isEditor && (
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-note-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {note.summary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight">Resumen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{note.summary}</p>
              </CardContent>
            </Card>
          )}

          {note.raw_notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight">Notas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{note.raw_notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {note.attendees && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-medium tracking-tight">Asistentes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{note.attendees}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium tracking-tight">Relacionado con</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {company && (
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    <Briefcase className="w-3 h-3" />
                    Empresa
                  </div>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-sm"
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    {company.name}
                  </Button>
                </div>
              )}
              {contact && (
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    <User className="w-3 h-3" />
                    Contacto
                  </div>
                  <div className="text-sm">{contact.first_name || contact.name} {contact.last_name || ''}</div>
                  <div className="text-xs text-muted-foreground">{contact.role}</div>
                </div>
              )}
              {opportunity && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Oportunidad
                  </div>
                  <div className="text-sm">{opportunity.name}</div>
                  <Badge className="mt-1">{opportunity.stage}</Badge>
                </div>
              )}
              {!company && !contact && !opportunity && (
                <p className="text-sm text-muted-foreground">Sin entidades relacionadas</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium tracking-tight">Metadatos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {note.creator && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Creador</div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    {note.creator}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Creado</div>
                <div>{new Date(note.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Última actualización</div>
                <div>{new Date(note.updated_at).toLocaleString()}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <TextNoteDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        note={note}
        onSuccess={fetchNoteData}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Eliminar Nota de Texto"
        description="¿Estás seguro de que quieres eliminar esta nota? Esta acción no se puede deshacer."
      />
    </div>
  );
};

export default TextNoteDetail;
